import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import Container from '@mui/material/Container';

import Button from '@mui/material/Button';
import MailOutline from '@mui/icons-material/MailOutline';
import Copyright from '../../components/Copyright';
import { useNotification } from '../../context/NotificationContext';
import {
  WEBSITE_NAME,
  SUPPORT_EMAIL,
  WEBSITE_LINK,
  PRODUCT_NAME,
} from '../../common/AppConstants';

function preventDefault(event) {
  event.preventDefault();
}

const termsList = [
  {
    title: 'Information We Collect',
    content:
      'We collect the following types of data when you use our services.',
    children: [
      {
        content: 'Information You Provide, including but not limited to:',
        listItems: [
          'Name',
          'Email Address',
          'Account Credentials',
          'School or University Information',
          'Information about employees, students, etc., of your organization',
          'Any other information you voluntarily provide',
        ],
      },
      {
        content: 'Automatically Collected Information, such as:',
        listItems: [
          'IP Address',
          'Browser Type & Version',
          'Device Information',
          'Usage Data (e.g., pages visited, features used, session duration)',
          'Cookies and Tracking Technologies',
        ],
      },
      {
        content: 'Information from Third-Party Services, such as:',
        listItems: [
          'Analytics providers (e.g., Google Analytics)',
          'Payment processors (if applicable)',
        ],
      },
      {
        content:
          'If you provide information about third parties (e.g., employees, students), you confirm that you have obtained their consent where required by law.',
        listItems: [],
      },
    ],
  },
  {
    title: 'How We Use Your Information',
    content: '',
    children: [
      {
        content: 'We use your data for the following purposes:',
        listItems: [
          'To provide and maintain our services',
          'To personalize and improve user experience',
          'To send service-related notifications (e.g., updates, account changes)',
          'To respond to inquiries and provide customer support',
          'To analyze and enhance platform performance',
          'To comply with legal obligations',
        ],
      },
      {
        content:
          'Our legal basis for processing personal data includes consent, contract necessity, legal obligations, or legitimate interests, as applicable.',
        listItems: [],
      },
      {
        content:
          'We do not use your personal data for automated decision-making or profiling that has a significant impact on you.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Sharing Your Information',
    content: 'We do not sell, rent, or trade your personal data.',
    children: [
      {
        content: 'However, we may share it in the following circumstances:',
        listItems: [
          'With Service Providers: We work with trusted third parties (e.g., hosting, analytics, email services) who process your data on our behalf under strict confidentiality agreements.',
          'For Legal Compliance: We may disclose information if required by law, court order, regulatory requirement, or to protect our rights, interests, and security.',
          'In Business Transfers: If we are involved in a merger, acquisition, or sale, your data may be transferred as part of the business transition.',
        ],
      },
    ],
  },
  {
    title: 'Data Security',
    content: '',
    children: [
      {
        content: 'We take reasonable measures to protect your data, including:',
        listItems: [
          'Encryption of sensitive data',
          'Secure access controls and authentication',
          'Regular security audits and vulnerability testing',
        ],
      },
      {
        content:
          'Despite our best efforts, no system is 100% secure. Therefore, we cannot guarantee its absolute security. We recommend using strong passwords and being cautious while sharing personal information online.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Your Rights & Choices',
    content: '',
    children: [
      {
        content:
          'Depending on your location or jurisdiction, you may have the following rights under GDPR, CCPA, or other privacy laws:',
        listItems: [
          'Access & Portability: Request a copy of your data.',
          'Correction: You can update or correct inaccurate or outdated data on our platform.',
          'Deletion: Request that we delete your data, except where retention is legally or contractually necessary.',
          'Opt-Out: Restrict certain uses of your data (e.g., marketing emails).',
        ],
      },
      {
        content:
          'To exercise your rights, contact us at {SUPPORT_EMAIL}. We will respond to your request within a reasonable timeframe.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Data Retention Policy',
    content:
      'We retain your personal data only as long as necessary to fulfill the purposes outlined in this policy and our Terms Of Service avaliable at {WEBSITE_LINK}terms-of-service. We retain data for as long as necessary for legal, regulatory, and business purposes. Upon expiry of the retention period, data will be securely deleted or anonymized.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Minors & Age Restrictions',
    content:
      "Our services are not intended for children under 13 (or under 16 in some jurisdictions). We do not knowingly collect or process children's personal data without appropriate parental consent where required. If we become aware that a child has provided personal information, we will take steps to delete it.",
    children: [
      {
        content:
          'If you believe a minor has shared data with us, please contact {SUPPORT_EMAIL} immediately.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Governing Law & Jurisdiction',
    content:
      'This Privacy Policy shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Delhi, India.',
    children: [
      {
        content:
          'Before pursuing legal action, users agree to attempt to resolve disputes through mediation or arbitration.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Changes to This Privacy Policy',
    content:
      'We reserve the right to update this Privacy Policy at any time. Users are responsible for reviewing them regularly. Continued use of the service after changes means you accept them.',
    children: [
      {
        content:
          'We will notify users of significant changes via email or website notifications.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Contact Us',
    content:
      'For any questions or concerns regarding this Privacy Policy, please contact us at {SUPPORT_EMAIL}.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
];

const replacePlaceholders = (text) => {
  if (!text) return text; // Handle empty strings gracefully
  return text
    .replaceAll('{SUPPORT_EMAIL}', SUPPORT_EMAIL)
    .replaceAll('{WEBSITE_LINK}', WEBSITE_LINK)
    .replaceAll('{WEBSITE_NAME}', WEBSITE_NAME)
    .replaceAll('{PRODUCT_NAME}', PRODUCT_NAME);
};

export default function PrivacyPolicy() {
  const { addNotification } = useNotification();

  return (
    <Container component="main" maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Privacy Policy
      </Typography>
      <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 5 }}>
        Welcome to {WEBSITE_NAME}. Your privacy is important to us. This Privacy
        Policy explains how we collect, use, share, and protect your personal
        information when you use our services. By accessing or using our
        platform, you acknowledge and agree to the terms of this Privacy Policy.
        If you do not agree, you must discontinue the use of our services
        immediately.
      </Typography>

      {termsList.map((section, section_index) => (
        <Box key={section_index}>
          <Typography variant="h5" sx={{ mt: 3 }}>
            {section_index + 1}
            {'. '}
            {replacePlaceholders(section.title)}
          </Typography>
          <Typography variant="body1">
            {replacePlaceholders(section.content)}
          </Typography>

          {section.children.map((children, children_index) => (
            <Box key={section_index}>
              <Typography variant="body1" sx={{ pt: 1, pb: 0, pl: 0, pr: 0 }}>
                {replacePlaceholders(children.content)}
              </Typography>
              <List disablePadding>
                {children.listItems.map((item, item_index) => (
                  <ListItem disablePadding key={item_index} sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {'* '}
                          {replacePlaceholders(item)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      ))}

      <Typography variant="h5" sx={{ mt: 3 }}>
        Last Updated: 19th February, 2025
      </Typography>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
